import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Shield,
  Crown,
  Users,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

import { API_URL } from '../config';

export default function AdminPage() {
  const { user, token } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/subscription/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setSubscriptionStatus(data);
        }
      } catch (error) {
        console.error('Erreur:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user && token) {
      fetchStatus();
    }
  }, [user, token]);

  const isAdmin = user?.role === 'admin' || subscriptionStatus?.is_admin;

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--bg))] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cyan-300">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-6">
        <div className="max-w-2xl mx-auto">
          <Alert className="bg-red-500/10 border-red-500/30">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <AlertDescription className="text-[hsl(var(--text))]">
              <p className="font-semibold">Accès non autorisé</p>
              <p className="text-sm">Cette page est réservée aux administrateurs.</p>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="admin-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Shield className="w-10 h-10 text-yellow-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-amber-300 bg-clip-text text-transparent">
              Panneau Administrateur
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm">
            Gestion des accès et informations système
          </p>
        </div>

        {/* Status Admin */}
        <Alert className="bg-green-500/10 border-green-500/30">
          <CheckCircle className="w-4 h-4 text-green-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <p className="font-semibold text-green-300">✅ Accès Administrateur Activé</p>
            <p className="text-sm text-[hsl(var(--text-dim))] mt-1">
              Vous bénéficiez d'un accès gratuit et illimité à toutes les fonctionnalités de l'application.
            </p>
          </AlertDescription>
        </Alert>

        {/* Infos Compte */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Users className="w-5 h-5" />
              Informations du Compte
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-[hsl(var(--bg))]">
                <p className="text-sm text-[hsl(var(--text-dim))] mb-1">Nom</p>
                <p className="font-semibold text-[hsl(var(--text))]">{user?.name}</p>
              </div>
              
              <div className="p-4 rounded-lg bg-[hsl(var(--bg))]">
                <p className="text-sm text-[hsl(var(--text-dim))] mb-1">Email</p>
                <p className="font-semibold text-[hsl(var(--text))]">{user?.email}</p>
              </div>
              
              <div className="p-4 rounded-lg bg-[hsl(var(--bg))]">
                <p className="text-sm text-[hsl(var(--text-dim))] mb-1">Rôle</p>
                <Badge className="bg-yellow-500 text-black">
                  <Shield className="w-3 h-3 mr-1" />
                  Administrateur
                </Badge>
              </div>
              
              <div className="p-4 rounded-lg bg-[hsl(var(--bg))]">
                <p className="text-sm text-[hsl(var(--text-dim))] mb-1">Type d'Abonnement</p>
                <Badge className="bg-green-500 text-white">
                  <Crown className="w-3 h-3 mr-1" />
                  Accès Gratuit Admin
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Privilèges Admin */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Crown className="w-5 h-5" />
              Privilèges Administrateur
            </CardTitle>
            <CardDescription className="text-[hsl(var(--text-dim))]">
              Accès et fonctionnalités disponibles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Accès illimité à toutes les fonctionnalités',
                'Chat IA illimité sans restriction',
                'Tous les calculateurs de dose',
                'Protocoles d\'urgence complets',
                'Aide au diagnostic avancée',
                'Assistante de discussion médicale en RealTime',
                'Tous les gestes techniques',
                'Exportation PDF des protocoles',
                'Historique complet des consultations',
                'Support prioritaire',
                'Aucun frais - Accès 100% gratuit'
              ].map((privilege, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-[hsl(var(--bg))] hover:bg-cyan-500/5 transition-colors">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-[hsl(var(--text))]">{privilege}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Info Status */}
        {subscriptionStatus && (
          <Card className="bg-[hsl(var(--bg-elev))] border-yellow-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-300">
                <Shield className="w-5 h-5" />
                Statut du Système
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--text-dim))]">Statut :</span>
                  <Badge className="bg-green-500 text-white">{subscriptionStatus.status}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--text-dim))]">Type :</span>
                  <span className="text-[hsl(var(--text))] font-semibold">{subscriptionStatus.subscription_type || subscriptionStatus.plan}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--text-dim))]">Accès :</span>
                  <span className="text-green-400 font-semibold">✅ Illimité</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
